import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useMemo } from 'react'
import { getBaseApiUrl } from '../../../../services/api.service'
import { CustomerProfileRequest, CustomerProfileResponse } from '../../../../api/models'

const baseApiUrl = getBaseApiUrl('/v1/customer-profile')

export type CustomerProfileResponseRow = CustomerProfileResponse & { name: string }

function useCustomerProfile(customerId: number | null, isAdmin: boolean) {
  const queryClient = useQueryClient()

  const { data: customerProfileById } = useQuery({
    queryKey: ['/v1/customer-profile/customer', customerId],
    queryFn: async () => (await axios.get<{ data: CustomerProfileResponse[] }>(`${baseApiUrl}/customer/${customerId}`)).data.data,
    staleTime: Infinity, // never stale
    enabled: !!customerId && isAdmin,
  })
  const { data: customerProfilesForCurrentUser } = useQuery({
    queryKey: ['/v1/customer-profile/customer'],
    queryFn: async () => (await axios.get<{ data: CustomerProfileResponse[] }>(`${baseApiUrl}/customer`)).data.data,
    staleTime: Infinity, // never stale
    enabled: !!customerId && !isAdmin,
  })

  const { data: publicCustomerCustomerProfilesData } = useQuery({
    queryKey: ['/v1/customer-profile/public'],
    queryFn: async () => (await axios.get<{ data: CustomerProfileResponse[] }>(`${baseApiUrl}/public`)).data.data,
    staleTime: Infinity, // never stale
    enabled: !!customerId && isAdmin,
  })
  const postCustomerProfile = async (request: CustomerProfileRequest) => {
    const created = (await axios.post<CustomerProfileResponse>(`${baseApiUrl}`, request)).data
    queryClient.invalidateQueries({ queryKey: ['/v1/customer-profile'] })
    queryClient.invalidateQueries({ queryKey: ['/v1/customer-profile/customer'] })
    queryClient.invalidateQueries({
      queryKey: ['/v1/customer', `${request.customerId}`],
    })
    return created
  }
  const putCustomerProfile = async (request: CustomerProfileRequest) => {
    const updated = (await axios.put<CustomerProfileResponse>(`${baseApiUrl}`, request)).data
    queryClient.invalidateQueries({ queryKey: ['/v1/customer-profile'] })
    queryClient.invalidateQueries({ queryKey: ['/v1/customer-profile/customer'] })
    queryClient.invalidateQueries({
      queryKey: ['/v1/customer', request.customerId],
    })
    return updated
  }

  const removeAccessToProfile = async (id: number) => {
    await axios.delete<void>(`${baseApiUrl}/${id}`)
    queryClient.invalidateQueries({ queryKey: ['/v1/customer-profile'] })
    queryClient.invalidateQueries({ queryKey: ['/v1/customer-profile/customer'] })
    queryClient.invalidateQueries({
      queryKey: ['/v1/customer'],
      exact: false,
    })
  }
  const profiles = useMemo(() => {
    if (isAdmin) {
      return customerProfileById ?? []
    }
    return customerProfilesForCurrentUser ?? []
  }, [customerProfileById, customerProfilesForCurrentUser, isAdmin])

  const publicCustomerProfiles = useMemo(() => publicCustomerCustomerProfilesData ?? [], [publicCustomerCustomerProfilesData])

  return {
    profiles,
    removeAccessToProfile,
    publicCustomerProfiles,
    postCustomerProfile,
    putCustomerProfile,
  }
}

export default useCustomerProfile
